import { useEffect, RefObject } from 'react'

/**
 * Horizontal tab strips scroll on narrow viewports, and the browser starts them at scrollLeft 0 - so
 * the current tab is off-screen on first paint whenever it is not one of the first few. Centre it in
 * the strip on mount and whenever the selection changes.
 *
 * Positions are measured from bounding rects rather than `offsetLeft`, because the strip is
 * `position: static` at these widths and so is not its own offset parent. Scrolling the container
 * directly rather than calling `scrollIntoView` also keeps the page's vertical position alone.
 *
 * The strip is not necessarily final when the effect first runs: tabs are gated on data that arrives
 * later, and before it does the strip can still fit, so a single pass measures a strip that does not
 * need scrolling and does nothing. Hence the retries - each is a no-op once the tab is in view.
 */
export default function useScrollSelectedIntoView(
  containerRef: RefObject<HTMLElement | null>,
  selectedRef: RefObject<HTMLElement | null>,
  selectionKey: string,
) {
  useEffect(() => {
    let frame = 0
    const timers: number[] = []

    const centre = () => {
      const container = containerRef.current
      const selected = selectedRef.current
      if (!container || !selected) return
      if (container.scrollWidth <= container.clientWidth + 1) return

      const containerRect = container.getBoundingClientRect()
      const selectedRect = selected.getBoundingClientRect()
      const offsetInStrip = selectedRect.left - containerRect.left
      const centred = offsetInStrip - (container.clientWidth - selectedRect.width) / 2
      const target = Math.max(0, Math.min(container.scrollWidth - container.clientWidth, container.scrollLeft + centred))
      if (Math.abs(target - container.scrollLeft) > 1) container.scrollLeft = target
    }

    centre()
    frame = requestAnimationFrame(centre)
    for (const delay of [150, 600]) timers.push(window.setTimeout(centre, delay))

    return () => {
      cancelAnimationFrame(frame)
      for (const t of timers) clearTimeout(t)
    }
  }, [containerRef, selectedRef, selectionKey])
}
